import React from 'react'
import { Show, useShowContext, SimpleShowLayout, useTranslate } from 'react-admin'
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'
import dayjs from 'dayjs'
import 'dayjs/locale/es'

dayjs.locale('es')

// Estilos base para los chips de estado
const baseStatusStyle = {
  borderRadius: '5px',
  color: 'white',
  padding: '4px 12px',
  fontSize: 14,
  textAlign: 'center',
  display: 'inline-block',
  fontWeight: 500,
}

// Colores para estado general (con espacios como viene de la API)
const getStatusGeneralStyle = (status) => {
  const colors = {
    'ERROR': '#ff0000',
    'ELIMINADO': '#ff0000',
    'EN SITIO': '#ffff00',
    'CANCELADO': '#ffff00',
    'RESERVADO': '#22ee65',
  }
  const backgroundColor = colors[status] || '#gray'
  const textColor = ['#ffff00'].includes(backgroundColor) ? '#000' : '#fff'
  return { ...baseStatusStyle, backgroundColor, color: textColor }
}

// Colores para estado flow (con espacios como viene de la API)
const getStatusFlowStyle = (status) => {
  const colors = {
    'BOOKING FALLIDO': '#ff0000',
    'SOLICITUD CREADA': '#22ee65',
    'PTP TREN CREADO': '#22ee65',
    'PASE TREN CREADO': '#22ee65',
    'PAGO EN PROCESO': '#22ee65',
    'AEREO CREADO': '#22ee65',
    'FRAUDE': '#ffff00',
    'SESION EXPIRADA': '#ffff00',
    'SESION CERRADA': '#ffff00',
    'PAGO FALLIDO': '#ffff00',
    'CREADO': '#ffa500',
  }
  const backgroundColor = colors[status] || '#gray'
  const textColor = ['#ffff00'].includes(backgroundColor) ? '#000' : '#fff'
  return { ...baseStatusStyle, backgroundColor, color: textColor }
}

// Parsear datetime en formato YYYYMMDDHHmmss
const parseStatusLogDatetime = (datetime) => {
  if (!datetime || datetime.length !== 14) return null
  const year = datetime.substring(0, 4)
  const month = datetime.substring(4, 6)
  const day = datetime.substring(6, 8)
  const hour = datetime.substring(8, 10)
  const minute = datetime.substring(10, 12)
  return dayjs(`${year}-${month}-${day} ${hour}:${minute}`)
}

// Componente para mostrar el historial de estados
const HistoryStatusTable = ({ statusLogs, loading }) => {
  const translate = useTranslate()

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={2}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (!statusLogs || statusLogs.length === 0) {
    return (
      <Typography variant='body2' color='textSecondary'>
        {translate('ra.message.no_history')}
      </Typography>
    )
  }

  return (
    <TableContainer component={Paper} variant='outlined'>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{translate('ra.field.date_time')}</TableCell>
            <TableCell>{translate('ra.field.general_state')}</TableCell>
            <TableCell>{translate('ra.field.flow_status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statusLogs.map((item, index) => {
            const parsedDate = parseStatusLogDatetime(item.datetime)
            return (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant='body2' color='textSecondary'>
                    {parsedDate ? parsedDate.format('ddd, DD MMM YYYY, HH:mm') : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.status_general ? (
                    <Box component='span' sx={getStatusGeneralStyle(item.status_general)}>
                      {item.status_general}
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  {item.status_flow ? (
                    <Box component='span' sx={getStatusFlowStyle(item.status_flow)}>
                      {item.status_flow}
                    </Box>
                  ) : '-'}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// Componente para mostrar los segmentos en filas
const SegmentsList = ({ segments, operatingCarriers, marketingCarriers, translate }) => {
  if (!segments || segments.length === 0) return '-'

  const formatSegmentDateTime = (dateString) => {
    if (!dateString) return '-'
    return dayjs(dateString).format('DD/MM/YYYY HH:mm')
  }

  return (
    <TableContainer component={Paper} variant='outlined' sx={{ mt: 1 }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>{translate('ra.field.segment')}</TableCell>
            <TableCell>{translate('ra.field.origin')}</TableCell>
            <TableCell>{translate('ra.field.destination')}</TableCell>
            <TableCell>{translate('ra.field.departure')}</TableCell>
            <TableCell>{translate('ra.field.arrival')}</TableCell>
            <TableCell>{translate('ra.field.operating_carrier')}</TableCell>
            <TableCell>{translate('ra.field.marketing_carrier')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {segments.map((segment, index) => (
            <TableRow key={index}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>{segment?.origin_station || '-'}</TableCell>
              <TableCell>{segment?.destination_station || '-'}</TableCell>
              <TableCell>{formatSegmentDateTime(segment?.departure_datetime)}</TableCell>
              <TableCell>{formatSegmentDateTime(segment?.arrival_datetime)}</TableCell>
              <TableCell>{operatingCarriers?.[index] || '-'}</TableCell>
              <TableCell>{segment?.carrier || marketingCarriers?.[index] || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const ShowContent = () => {
  const { isLoading, record } = useShowContext()
  const translate = useTranslate()

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    return dayjs(dateString).format('DD/MM/YYYY HH:mm')
  }

  const formatCurrency = (amount, currency) => {
    if (amount === null || amount === undefined) return '-'
    return `${currency || ''} ${amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const getProductTypeLabel = () => {
    if (record?.product_type) {
      const types = {
        'TRAINS': 'Trenes Tickets',
        'TRAINS_PASS': 'Trenes Pases',
        'AIR': 'Aéreo',
        'CARS': 'Autos',
      }
      return types[record.product_type] || record.product_type
    }
    const subProducts = {
      'PTP': 'Ticket Trenes',
      'PASSES': 'Trenes Pases',
    }
    return subProducts[record?.sub_product] || 'Trenes'
  }

  const isPassesProduct = () => {
    return record?.sub_product === 'PASSES' || record?.product_type === 'TRAINS_PASS'
  }

  const formatDateRange = (validity) => {
    if (!validity?.from || !validity?.to) return '-'
    return `${dayjs(validity.from).format('DD/MM/YYYY')} - ${dayjs(validity.to).format('DD/MM/YYYY')}`
  }

  const formatDuration = (duration) => {
    if (!duration) return '-'
    return `${duration} días`
  }

  const goToReservation = () => {
    const reservationId = record?.b2_internal_number
    if (reservationId) {
      window.open(`/#/reservation/${reservationId}/show`, '_blank')
    }
  }

  const hasReservation = !!record?.b2_internal_number

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.operation-bff')} actions={null}>
        <SimpleShowLayout>
          {/* Encabezado: Id y Unidad de Trabajo */}
          <Grid container spacing={4} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant='body2' color='textSecondary'>
                Id:
              </Typography>
              <Typography variant='body1' fontWeight={500}>
                {isLoading ? <CircularProgress size={16} /> : (record?.operation_id || '-')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant='body2' color='textSecondary'>
                {translate('ra.field.work_unit')}:
              </Typography>
              <Typography variant='body1' color='primary' fontWeight={500}>
                {isLoading ? <CircularProgress size={16} /> : (record?.work_unit || '-')}
              </Typography>
            </Grid>
          </Grid>

          {/* Botón Ver Reserva */}
          {hasReservation && (
            <Box mb={3}>
              <Button onClick={goToReservation} variant='contained' color='primary'>
                {translate('ra.action.view_reservation')}
              </Button>
            </Box>
          )}

          {/* Sección Historial */}
          <Typography variant='h6' gutterBottom>
            {translate('ra.title.history')}
          </Typography>
          <HistoryStatusTable
            statusLogs={record?.status_logs}
            loading={isLoading}
          />

          <Divider sx={{ my: 3 }} />

          {/* Sección Detalle del Producto (Trenes) */}
          <Typography variant='h6' gutterBottom>
            {getProductTypeLabel()}
          </Typography>

          <Card variant='outlined' sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                {/* Información del viaje */}
                <Grid item xs={12}>
                  <Typography variant='subtitle2' color='primary' gutterBottom>
                    {translate('ra.title.trip_info')}
                  </Typography>
                </Grid>

                {/* Campos para PTP */}
                {!isPassesProduct() && (
                  <>
                    <ShowListElement
                      label={translate('ra.field.device')}
                      value={record?.device}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.currency')}
                      value={record?.currency}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.passenger_count')}
                      value={record?.passenger_count}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.supplier')}
                      value={record?.supplier}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.segment_count')}
                      value={record?.segment_count}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.provider_codes')}
                      value={record?.booking_references?.join(', ')}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.origin')}
                      value={record?.origin_station}
                      loading={isLoading}
                      size={6}
                    />
                    <ShowListElement
                      label={translate('ra.field.destination')}
                      value={record?.destination_station}
                      loading={isLoading}
                      size={6}
                    />
                    <ShowListElement
                      label={translate('ra.field.departure')}
                      value={formatDateTime(record?.departure_datetime)}
                      loading={isLoading}
                      size={6}
                    />
                    <ShowListElement
                      label={translate('ra.field.arrival')}
                      value={formatDateTime(record?.arrival_datetime)}
                      loading={isLoading}
                      size={6}
                    />

                    {/* Segmentos */}
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                        {translate('ra.field.carriers')}
                      </Typography>
                      <SegmentsList
                        segments={record?.segments}
                        operatingCarriers={record?.operating_carriers}
                        marketingCarriers={record?.marketing_carriers}
                        translate={translate}
                      />
                    </Grid>
                  </>
                )}

                {/* Campos para PASSES */}
                {isPassesProduct() && (
                  <>
                    {/* Fila 1: 6 campos con size={2} */}
                    <ShowListElement
                      label={translate('ra.field.device')}
                      value={record?.device}
                      loading={isLoading}
                      size={2}
                    />
                    <ShowListElement
                      label={translate('ra.field.currency')}
                      value={record?.currency}
                      loading={isLoading}
                      size={2}
                    />
                    <ShowListElement
                      label={translate('ra.field.passenger_count')}
                      value={record?.passenger_count}
                      loading={isLoading}
                      size={2}
                    />
                    <ShowListElement
                      label={translate('ra.field.supplier')}
                      value={record?.pass_supplier}
                      loading={isLoading}
                      size={2}
                    />
                    <ShowListElement
                      label={translate('ra.field.provider_codes')}
                      value={record?.booking_references?.join(', ')}
                      loading={isLoading}
                      size={2}
                    />
                    <ShowListElement
                      label={translate('ra.field.pass_marketing_carrier')}
                      value={record?.pass_marketing_carrier}
                      loading={isLoading}
                      size={2}
                    />
                    {/* Fila 2: 4 campos con size={3} */}
                    <ShowListElement
                      label={translate('ra.field.pass_name')}
                      value={record?.pass_name}
                      loading={isLoading}
                      size={3}
                    />
                    <ShowListElement
                      label={translate('ra.field.pass_validity')}
                      value={formatDateRange(record?.pass_validity)}
                      loading={isLoading}
                      size={3}
                    />
                    <ShowListElement
                      label={translate('ra.field.pass_duration')}
                      value={formatDuration(record?.pass_duration)}
                      loading={isLoading}
                      size={3}
                    />
                    <ShowListElement
                      label={translate('ra.field.pass_offer_type')}
                      value={record?.pass_offer_type}
                      loading={isLoading}
                      size={3}
                    />
                  </>
                )}

                {/* Información del pago */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant='subtitle2' color='primary' gutterBottom>
                    {translate('ra.title.payment_info')}
                  </Typography>
                </Grid>
                <ShowListElement
                  label={translate('ra.field.total_amount')}
                  value={formatCurrency(record?.total_amount, record?.currency)}
                  loading={isLoading}
                  size={4}
                />
                <ShowListElement
                  label={translate('ra.field.payment_attempts')}
                  value={record?.payment_attempts}
                  loading={isLoading}
                  size={4}
                />
                <ShowListElement
                  label={translate('ra.field.payment_status')}
                  value={record?.payment_status || '-'}
                  loading={isLoading}
                  size={4}
                />
                <ShowListElement
                  label={translate('ra.field.card_brand')}
                  value={record?.card_brand || '-'}
                  loading={isLoading}
                  size={4}
                />
                <ShowListElement
                  label={translate('ra.field.issuing_bank')}
                  value={record?.issuing_bank || '-'}
                  loading={isLoading}
                  size={4}
                />
                <ShowListElement
                  label={translate('ra.field.payment_gateway')}
                  value={record?.payment_gateway || '-'}
                  loading={isLoading}
                  size={4}
                />
                <ShowListElement
                  label={translate('ra.field.installments')}
                  value={record?.installments}
                  loading={isLoading}
                  size={4}
                />

                {/* Información de fraude (cuando esté disponible) */}
                {(record?.fraud_analysis || record?.fraud_detail || record?.fraud_id) && (
                  <>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant='subtitle2' color='primary' gutterBottom>
                        {translate('ra.title.fraud_info')}
                      </Typography>
                    </Grid>
                    <ShowListElement
                      label={translate('ra.field.fraud_analysis')}
                      value={record?.fraud_analysis || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.fraud_detail')}
                      value={record?.fraud_detail || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.fraud_id')}
                      value={record?.fraud_id || '-'}
                      loading={isLoading}
                      size={4}
                    />
                  </>
                )}

                {/* Estado de emisión (cuando esté disponible) */}
                {record?.emission_status && (
                  <ShowListElement
                    label={translate('ra.field.emission_status')}
                    value={record?.emission_status}
                    loading={isLoading}
                    size={4}
                  />
                )}

                {/* Datos del cliente (cuando estén disponibles) */}
                {record?.customer_data && (
                  <>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant='subtitle2' color='primary' gutterBottom>
                        {translate('ra.title.customer_data')}
                      </Typography>
                    </Grid>
                    <ShowListElement
                      label={translate('ra.field.first_name')}
                      value={record?.customer_data?.first_name || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.last_name')}
                      value={record?.customer_data?.last_name || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.email')}
                      value={record?.customer_data?.email || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.country')}
                      value={record?.customer_data?.country || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.state')}
                      value={record?.customer_data?.state || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.city')}
                      value={record?.customer_data?.city || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.phone')}
                      value={record?.customer_data?.phone || '-'}
                      loading={isLoading}
                      size={4}
                    />
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Sección Datos del Cliente */}
          {record?.client_data && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant='h6' gutterBottom>
                {translate('ra.title.client_data')}
              </Typography>
              <Card variant='outlined' sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <ShowListElement
                      label={translate('ra.field.first_name')}
                      value={record?.client_data?.first_name || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.last_name')}
                      value={record?.client_data?.last_name || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.title')}
                      value={record?.client_data?.title || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.age')}
                      value={record?.client_data?.age || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.passenger_type')}
                      value={record?.client_data?.type || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.date_of_birth')}
                      value={record?.client_data?.date_of_birth || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.doc_type')}
                      value={record?.client_data?.doc_type || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.doc_number')}
                      value={record?.client_data?.doc_number || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.country_of_residence')}
                      value={record?.client_data?.country_of_residence || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.phone_number')}
                      value={record?.client_data?.phone_number || '-'}
                      loading={isLoading}
                      size={4}
                    />
                    <ShowListElement
                      label={translate('ra.field.email_address')}
                      value={record?.client_data?.email_address || '-'}
                      loading={isLoading}
                      size={4}
                    />
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </SimpleShowLayout>
      </CardCommon>
    </Container>
  )
}

const ShowWrapper = ({ children }) => (
  <Container maxWidth={false}>{children}</Container>
)

export const OperationBffShow = (props) => {
  return (
    <Show component={ShowWrapper} {...props} className='CrudShowContainer' actions={null}>
      <ShowContent />
    </Show>
  )
}
